import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Briefcase, ListChecks, Inbox, TrendingUp, Handshake } from "lucide-react";

const STAGES = ["Sourcing", "Screening", "DD", "IC", "Closed Won", "Closed Lost"];

function formatUSD(n) {
  if (!n) return "$0";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function PageHeader({ overline, title }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream">
      <div className="overline text-gold-deep">{overline}</div>
      <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
      <div className="gold-rule mt-4" />
    </div>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "Pipeline Value", value: formatUSD(stats.pipeline_value_usd), icon: TrendingUp, accent: true },
        { label: "Closed Won", value: formatUSD(stats.closed_won_value_usd), icon: TrendingUp },
        { label: "Active Deals", value: stats.deals_total, icon: Briefcase },
        { label: "Contacts", value: stats.contacts_total, icon: Users },
        { label: "Open Tasks", value: stats.tasks_open, icon: ListChecks },
        { label: "Unread Messages", value: stats.messages_unread, icon: Inbox },
        { label: "Pending Consultations", value: stats.intros_pending ?? 0, icon: Handshake, accent: (stats.intros_pending ?? 0) > 0, href: "/dashboard/intros" },
      ]
    : [];

  return (
    <div data-testid="overview-page">
      <PageHeader overline="Control room" title="Overview" />
      <div className="p-8 space-y-10">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-4">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={i}
                data-testid={`kpi-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`bg-white border border-navy/10 p-5 ${c.accent ? "border-l-2 border-l-gold" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <div className="overline text-navy/60">{c.label}</div>
                  <Icon className="h-4 w-4 text-gold-deep" />
                </div>
                <div className="font-serif text-3xl text-navy mt-3">{c.value}</div>
              </div>
            );
          })}
        </div>

        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-navy/10 p-6" data-testid="stage-breakdown">
              <div className="overline text-gold-deep">Pipeline by stage</div>
              <h3 className="font-serif text-xl text-navy mt-1 mb-6">Stage breakdown</h3>
              <div className="space-y-4">
                {STAGES.map((s) => {
                  const d = stats.by_stage[s] || { count: 0, value: 0 };
                  const max = Math.max(1, ...STAGES.map((st) => stats.by_stage[st]?.value || 0));
                  const pct = (d.value / max) * 100;
                  return (
                    <div key={s}>
                      <div className="flex justify-between text-sm">
                        <span className="text-navy/80">{s}</span>
                        <span className="text-navy/60">{d.count} · {formatUSD(d.value)}</span>
                      </div>
                      <div className="h-1.5 bg-navy/10 mt-1.5 overflow-hidden">
                        <div className="h-full bg-gold transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white border border-navy/10 p-6" data-testid="distribution">
              <div className="overline text-gold-deep">Distribution</div>
              <h3 className="font-serif text-xl text-navy mt-1 mb-6">By country & sector</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="overline text-navy/60 mb-3">Countries</div>
                  {Object.entries(stats.by_country).slice(0, 8).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm py-1 border-b border-navy/5">
                      <span className="text-navy/80">{k}</span>
                      <span className="text-navy/60">{v}</span>
                    </div>
                  ))}
                  {Object.keys(stats.by_country).length === 0 && (
                    <div className="text-sm text-navy/40 italic">No deals yet</div>
                  )}
                </div>
                <div>
                  <div className="overline text-navy/60 mb-3">Sectors</div>
                  {Object.entries(stats.by_sector).slice(0, 8).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm py-1 border-b border-navy/5">
                      <span className="text-navy/80">{k}</span>
                      <span className="text-navy/60">{v}</span>
                    </div>
                  ))}
                  {Object.keys(stats.by_sector).length === 0 && (
                    <div className="text-sm text-navy/40 italic">No deals yet</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
